/**
 * Haptics helper — thin, no-op-on-web wrapper around @capacitor/haptics.
 *
 * • On the web (or any non-native runtime), every method silently does nothing.
 *   This lets components fire haptics() unconditionally without sprinkling
 *   `if (isNative)` checks across the codebase.
 * • On iOS / Android (inside the Capacitor shell), it forwards to the native
 *   Taptic Engine / vibrator via Capacitor's bridge.
 * • All native calls are wrapped in try/catch so a failed bridge call (e.g.
 *   missing plugin registration on an old install) never breaks UX.
 *
 * Usage:
 *   import { haptic } from "../lib/haptics";
 *   haptic.tap();           // light impact — taps, menu opens, toggle clicks
 *   haptic.medium();        // confirmation — survey submit, save success
 *   haptic.success();       // notification success — raffle eligibility, big win
 *   haptic.warn();          // notification warning — validation error
 *   haptic.selection();     // tiny tick — list item highlight, radio change
 */

let _hapticsModule = null;
let _moduleLoading = null;

function _isNative() {
  try {
    return typeof window !== "undefined"
      && window.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

async function _loadModule() {
  if (_hapticsModule) return _hapticsModule;
  if (_moduleLoading) return _moduleLoading;
  // Lazy dynamic import — keeps the plugin out of the initial web bundle.
  _moduleLoading = import(/* webpackChunkName: "haptics" */ "@capacitor/haptics")
    .then((m) => { _hapticsModule = m; return m; })
    .catch(() => null);
  return _moduleLoading;
}

async function _impact(styleKey) {
  if (!_isNative()) return;
  const m = await _loadModule();
  if (!m?.Haptics || !m?.ImpactStyle) return;
  try { await m.Haptics.impact({ style: m.ImpactStyle[styleKey] }); } catch { /* swallow */ }
}

async function _notify(typeKey) {
  if (!_isNative()) return;
  const m = await _loadModule();
  if (!m?.Haptics || !m?.NotificationType) return;
  try { await m.Haptics.notification({ type: m.NotificationType[typeKey] }); } catch { /* swallow */ }
}

async function _selection() {
  if (!_isNative()) return;
  const m = await _loadModule();
  if (!m?.Haptics) return;
  try { await m.Haptics.selectionChanged(); } catch { /* swallow */ }
}

export const haptic = {
  tap:       () => _impact("Light"),
  medium:    () => _impact("Medium"),
  heavy:     () => _impact("Heavy"),
  success:   () => _notify("Success"),
  warn:      () => _notify("Warning"),
  error:     () => _notify("Error"),
  selection: () => _selection(),
};

export default haptic;
